import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { MinimalAlert, MinimalButton, MinimalCard, MinimalDropdown, MinimalFormSection, MinimalInput, type MinimalOption } from '@ovasabi/ui-minimal'
import { commandPayload, commands } from '../../lib/runtime'
import { ChowModal } from '../../components/chow'
import type { StoreRecord } from './chefShared'
import { readVerificationPaused, useMealCategories } from './mealCategories'

export type KitchenLocation = StoreRecord & { id: string; label: string; address: string; area: string; city: string }
export const completeKitchen = (row: StoreRecord) => Boolean(String(row.address ?? '').trim() && String(row.area ?? '').trim() && String(row.city ?? '').trim())
export const kitchenAddress = (row: StoreRecord) => [row.address, row.area, row.city].filter(Boolean).join(', ')

export const AddKitchenLocationModal = ({
  profileId,
  open,
  onClose,
  onSaved,
}: {
  profileId: string
  open: boolean
  onClose: () => void
  onSaved?: (loc: KitchenLocation) => void
}) => {
  const [label, setLabel] = useState('Main Kitchen')
  const [address, setAddress] = useState('')
  const [area, setArea] = useState('')
  const [city, setCity] = useState('Abuja')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  if (!open) return null

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!address.trim() || !area.trim() || !city.trim()) {
      setError('Please provide street address, area, and city.')
      return
    }
    setBusy(true)
    setError('')
    try {
      const payload = {
        label: label.trim() || 'Main Kitchen',
        address: address.trim(),
        area: area.trim(),
        city: city.trim(),
        is_primary: true,
      }
      const resp = await commands.profile.setVendorLocation(profileId, payload)
      const loc = commandPayload<KitchenLocation>(resp)
      onSaved?.(loc)
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save kitchen address.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <ChowModal title="Add kitchen address" open={open} onClose={onClose}>
      <form onSubmit={(e) => void submit(e)} style={{ display: 'grid', gap: '12px' }}>
        <MinimalInput
          label="Location name"
          required
          placeholder="e.g. Main Kitchen"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
        />
        <MinimalInput
          label="Street address"
          required
          hint="Where you cook. Customers collect their orders here."
          placeholder="e.g. 14 Aminu Kano Crescent"
          value={address}
          onChange={(e) => setAddress(e.target.value)}
        />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
          <MinimalInput
            label="Area"
            required
            placeholder="e.g. Wuse 2"
            value={area}
            onChange={(e) => setArea(e.target.value)}
          />
          <MinimalInput
            label="City"
            required
            placeholder="e.g. Abuja"
            value={city}
            onChange={(e) => setCity(e.target.value)}
          />
        </div>
        {error && <MinimalAlert tone="danger">{error}</MinimalAlert>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '4px' }}>
          <MinimalButton variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </MinimalButton>
          <MinimalButton variant="primary" type="submit" disabled={busy}>
            {busy ? 'Saving…' : 'Save address'}
          </MinimalButton>
        </div>
      </form>
    </ChowModal>
  )
}

export const useKitchenCatalog = (profileId: string, live = true) => {
  const [locations, setLocations] = useState<KitchenLocation[]>([])
  const categories = useMealCategories()
  const [paused, setPaused] = useState(true)
  const [loading, setLoading] = useState(live)
  const [error, setError] = useState('')
  const [revision, setRevision] = useState(0)
  useEffect(() => {
    if (!live) return
    let active = true
    setLoading(true)
    void Promise.all([readVerificationPaused(revision > 0), commands.profile.listVendorLocations(profileId)])
      .then(([verificationPaused, locationResponse]) => {
        if (!active) return
        const rows = commandPayload(locationResponse).locations
        if (!Array.isArray(rows)) throw new Error('Kitchen setup could not be loaded.')
        setLocations(rows.filter(completeKitchen) as KitchenLocation[])
        setPaused(verificationPaused)
        setError('')
      })
      .catch((reason) => { if (active) setError(reason instanceof Error ? reason.message : 'Could not load kitchen setup.') })
      .finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [profileId, live, revision])
  const reload = useCallback(() => setRevision((value) => value + 1), [])
  return { locations, categories, paused, loading, error, reload }
}

export const useKitchenLocations = useKitchenCatalog

/** Most categories the picker draws at once; search reaches the rest. */
export const CATEGORY_PICKER_LIMIT = 30

/**
 * The one category field every dish form uses. The list can grow to 300
 * categories, so it is always searchable and draws at most
 * CATEGORY_PICKER_LIMIT rows; the selected category is always shown.
 */
export const CategoryPicker = ({ options, value, onChange }: {
  options: readonly MinimalOption<string>[]
  value: string
  onChange: (next: string) => void
}) => (
  <MinimalDropdown
    label={<>Category <span aria-hidden="true">*</span></>}
    options={options}
    value={value}
    onChange={onChange}
    searchable
    searchPlaceholder="Search categories…"
    maxVisibleOptions={CATEGORY_PICKER_LIMIT}
    panelMaxHeight={280}
  />
)

export const KitchenLocationRequired = ({ loading, error, reload }: { loading: boolean; error: string; reload: () => void }) => {
  const navigate = useNavigate()
  return <MinimalFormSection title="Add your kitchen location" description="A kitchen street address, area, and city are required before you create dishes or menus.">
    <MinimalAlert tone={error ? 'danger' : 'info'}>{error || (loading ? 'Loading your kitchen details…' : 'Open Profile → Kitchen locations. Add or update your address, then return here.')}</MinimalAlert>
    <MinimalButton onClick={() => navigate('/profile#kitchen-locations')}>Update kitchen location</MinimalButton>
    <MinimalButton variant="ghost" onClick={reload} disabled={loading}>Refresh kitchen details</MinimalButton>
  </MinimalFormSection>
}

export const CategoryRequestForm = ({ profileId }: { profileId: string }) => {
  const [label, setLabel] = useState('')
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState('')
  const [failed, setFailed] = useState(false)
  const submit = async () => {
    setBusy(true)
    try { await commands.menu.requestCategory({ chef_profile_id: profileId, label: label.trim(), reason: reason.trim() }); setMessage('Thanks. Our team will review your suggestion.'); setFailed(false); setLabel(''); setReason('') }
    catch (error) { setMessage(error instanceof Error ? error.message : 'Could not request a category.'); setFailed(true) }
    finally { setBusy(false) }
  }
  return <MinimalCard padding="sm"><MinimalFormSection title="Need another meal category?" description="Can’t find the right category? Suggest one and our team will review it.">
    <MinimalInput label="Requested category" required maxLength={100} value={label} onChange={(event) => setLabel(event.target.value)} />
    <MinimalInput label="Reason (optional)" maxLength={1000} value={reason} onChange={(event) => setReason(event.target.value)} />
    {message && <MinimalAlert tone={failed ? 'danger' : 'success'}>{message}</MinimalAlert>}
    <MinimalButton onClick={() => void submit()} disabled={busy || label.trim().length < 2}>Request category</MinimalButton>
  </MinimalFormSection></MinimalCard>
}
